import { useRef } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";

const REVIEWS = [
  {
    quote:
      "Angela made our experience with selling our home and buying another so much easier than we were expecting. We were dreading the process of selling in a timely manner to purchase the home we really wanted but were amazed at how things seemed to magically come together. Her patience, kindness, and determination helped relieve the stress of both transactions. We were truly blessed to have Angela as our realtor and if we ever decide to move again we will definitely go with Angela and her team.",
    author: "David",
    detail: "Sold in Turlock · 2024",
  },
  {
    quote:
      "Angela is an outstanding real estate agent. She has sold two homes for us over the past few years, and we couldn't be happier with the results. Our most recent sale was especially challenging because of a difficult HOA, but Angela never gave up. When they wouldn't respond to emails or phone calls, she went to their office herself to get the answers and documents the lender needed to keep the transaction moving. She kept us informed every step of the way, communicating clearly and making sure we always knew what was happening. Thanks to her persistence, professionalism, and dedication, we closed on time despite the obstacles. We are very grateful for everything Angela did for us and highly recommend her to anyone looking for a knowledgeable, hardworking, and truly dedicated real estate agent.",
    author: "Melinda T.",
    detail: "Sold in Modesto · 2023",
  },
  {
    quote:
      "Partial quote from Luke 10:7, \"…for the worker is worthy of his wages\"… while Jesus is instructing the disciples, nevertheless the lesson is: some humans do the work for reasons other than money/monetary gains, and that in itself deserves the monetary reward. Showing property to future clients without a sale (yet) is what Angela is all about — she believes in her desire to help her fellow humans obtain their dreams of ownership. Diligent, flexible, articulate and knowledgeable (sizing up a client, offering timely advice) — all attributes of an honorable saleswoman. If every sales person has the same goal of helping their client obtain a property, appliance or a car, this world will be a better place, at least for a while. Thank you Angela for your hard work and your patience, and I hope you get all the rewards that you deserve. Go team Watson!!",
    author: "Raymond Shamouel",
    detail: "Bought in Denair · 2024",
  },
  {
    quote:
      "Angela is the best! She has been truly amazing throughout every step of our real estate journey. We've sold one home and purchased two with her, and every experience has been exceptional. She makes the entire process feel easy and stress-free with her knowledge, professionalism, and dedication. We couldn't be happier and highly recommend Angela to anyone looking to buy or sell a home!",
    author: "Emily Preciado",
    detail: "Sold in Turlock · 2025",
  },
];

export default function Reviews() {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="relative bg-[#0A0A0A] py-32 md:py-44 border-t border-white/5 overflow-hidden">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex items-end justify-between mb-16 md:mb-20 gap-6">
          <div>
            <p className="overline text-[#CBBFAE] mb-6">Client Voices</p>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-white max-w-3xl">
              Words from our past <em className="text-[#CBBFAE]">homebuyers and sellers.</em>
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid="reviews-prev"
              onClick={() => scrollBy(-1)}
              aria-label="Previous"
              className="w-12 h-12 rounded-full border border-white/20 hover:border-[#CBBFAE] text-white/80 hover:text-[#CBBFAE] transition-colors flex items-center justify-center"
            >←</button>
            <button
              data-testid="reviews-next"
              onClick={() => scrollBy(1)}
              aria-label="Next"
              className="w-12 h-12 rounded-full border border-white/20 hover:border-[#CBBFAE] text-white/80 hover:text-[#CBBFAE] transition-colors flex items-center justify-center"
            >→</button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        data-testid="reviews-horizontal-scroll"
        className="no-scrollbar flex overflow-x-auto snap-x snap-mandatory gap-6 md:gap-10 px-6 md:px-12 pb-6"
      >
        {REVIEWS.map((r, i) => (
          <motion.figure
            key={i}
            data-testid={`review-card-${i}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: Math.min(i * 0.06, 0.4) }}
            className="relative snap-start shrink-0 w-[85vw] sm:w-[60vw] md:w-[38vw] lg:w-[30vw] border border-white/10 hover:border-[#CBBFAE]/50 transition-colors bg-[#0E0E0E] p-8 md:p-10 flex flex-col justify-between min-h-[360px]"
          >
            <div className="font-serif text-[6rem] leading-none text-[#CBBFAE]/25 absolute top-4 left-6 select-none">
              "
            </div>
            <blockquote className="relative font-serif text-xl md:text-2xl leading-snug text-white/90 mt-8">
              {r.quote}
            </blockquote>
            <figcaption className="mt-10 pt-6 border-t border-white/10">
              <div className="text-white font-medium">{r.author}</div>
              <div className="mt-2 flex items-center gap-1 text-[#CBBFAE]">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={16} strokeWidth={1.5} fill="currentColor" />
                ))}
              </div>
            </figcaption>
          </motion.figure>
        ))}
        <div className="shrink-0 w-4 md:w-12" />
      </div>

      <div className="mx-auto max-w-[1600px] px-6 md:px-12 mt-16 md:mt-20 flex justify-center">
        <a
          data-testid="reviews-google-link"
          href="https://www.google.com/search?q=Watson+Group+-+LPT+Realty&stick=H4sIAAAAAAAA_-NgU1I1qEi0MElONE81NkhKNDA1S7O0MqgwMk1OMjZOMzVINrFMNEo1XcQqGZ5YUpyfp-BelF9aoKCr4BMQohCUmphTUgkAgRuw0EUAAAA&hl=en&mat=CS-IoPG4p5CzElcBa0lj_2sKcHtDfkO9y21OJTt56wYZSXT_5FG9YVX2GDiuPgf9g-BgiSoFzw6BsvqlM7e7JLLlyzOnfctWTfFI1HeSoJUEoKR9cptA546qiEUdolHqeJw&authuser=0#mpd=~12939026142205085743/customers/reviews"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-4 border border-[#CBBFAE]/50 hover:border-[#CBBFAE] rounded-full pl-6 pr-2 py-2 text-[12px] tracking-[0.25em] uppercase text-white transition-all"
        >
          <span>Find Out More</span>
          <span className="w-9 h-9 rounded-full bg-[#CBBFAE] text-[#0A0A0A] flex items-center justify-center transition-transform group-hover:rotate-45">↗</span>
        </a>
      </div>
    </section>
  );
}
